import { useEffect, useMemo, useRef, useState, type ReactNode } from 'react'
import type { AuthenticatedUser } from '../../types/domain'
import { authService } from '../../services/supabase/auth'
import { AuthContext, type AuthContextValue, type SessionState } from './auth-context'

export function AuthProvider({ children }: { children: ReactNode }) {
  const [state, setState] = useState<SessionState>('loading')
  const [user, setUser] = useState<AuthenticatedUser | null>(null)
  const [error, setError] = useState('')
  const authGeneration = useRef(0)

  useEffect(() => {
    let active = true
    async function recover() {
      const generation = ++authGeneration.current
      try {
        const authenticated = await authService.recoverSession()
        if (!active || generation !== authGeneration.current) return
        setUser(authenticated)
        setState(authenticated ? 'authenticated' : 'anonymous')
        setError('')
      } catch (reason) {
        if (!active || generation !== authGeneration.current) return
        setError(reason instanceof Error ? reason.message : 'Não foi possível recuperar a sessão segura.')
        setState('error')
      }
    }

    void recover()
    const unsubscribe = authService.onAuthStateChange(() => void recover())
    const activeCheck = window.setInterval(() => {
      if (document.visibilityState === 'visible') void recover()
    }, 30_000)
    return () => {
      active = false
      window.clearInterval(activeCheck)
      unsubscribe()
    }
  }, [])

  const value = useMemo<AuthContextValue>(() => ({
    state,
    user,
    error,
    async signIn(email, password) {
      const authenticated = await authService.signInWithPassword(email, password)
      ++authGeneration.current
      setUser(authenticated)
      setState('authenticated')
      setError('')
      return authenticated
    },
    async signOut() {
      await authService.signOut()
      ++authGeneration.current
      setUser(null)
      setState('anonymous')
    },
    async refreshProfile() {
      const authenticated = await authService.recoverSession()
      if (!authenticated) throw new Error('Sua sessão expirou.')
      setUser(authenticated)
      setState('authenticated')
      return authenticated
    },
  }), [error, state, user])

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>
}
